/**
 * FE-14: Metrics Layout — Tab navigation for Growth / Funnel / Revenue / System / Behavior / Feature Value.
 * Default tab: Growth.
 */

import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GlobalFilters } from "../../components/GlobalFilters";
import { PageHeader } from "../../components/PageHeader";
import { t } from "../../i18n";
import { BehaviorTab } from "./BehaviorTab";
import { CohortTab } from "./CohortTab";
import { FeatureValueTab } from "./FeatureValueTab";
import { FunnelTab } from "./FunnelTab";
import { GrowthTab } from "./GrowthTab";
import { MessagesTab } from "./MessagesTab";
import { RevenueTab } from "./RevenueTab";
import { SegmentTab } from "./SegmentTab";
import { SystemTab } from "./SystemTab";

const TABS = [
  "Growth",
  "Funnel",
  "Revenue",
  "System",
  "Behavior",
  "Messages",
  "Feature Value",
  "Segments",
  "Cohort",
] as const;
type TabName = (typeof TABS)[number];

const TAB_I18N_KEYS: Record<TabName, string> = {
  Growth: "metrics.tab_growth",
  Funnel: "metrics.tab_funnel",
  Revenue: "metrics.tab_revenue",
  System: "metrics.tab_system",
  Behavior: "metrics.tab_behavior",
  Messages: "metrics.tab_messages",
  "Feature Value": "metrics.tab_feature_value",
  Segments: "metrics.tab_segments",
  Cohort: "metrics.tab_cohort",
};

function resolveTab(param: string | null): TabName {
  if (!param) return "Growth";
  const match = TABS.find((t) => t.toLowerCase() === param.toLowerCase());
  return match ?? "Growth";
}

export function MetricsLayout(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  // biome-ignore lint/correctness/useExhaustiveDependencies: read once on mount
  const initialTab = useMemo(() => resolveTab(searchParams.get("tab")), []);
  const [active, setActive] = useState<TabName>(initialTab);
  const highlight = searchParams.get("highlight") ?? undefined;
  const step = searchParams.get("step") ?? undefined;

  const handleTab = useCallback(
    (tab: TabName): void => {
      setActive(tab);
      setSearchParams({ tab }, { replace: true });
    },
    [setSearchParams],
  );

  return (
    <div>
      <PageHeader
        title={t("metrics.title")}
        description={t("metrics.description")}
      />
      <TabBar active={active} onSelect={handleTab} />
      <GlobalFilters />
      <div style={s.content}>
        {active === "Growth" && <GrowthTab highlight={highlight} />}
        {active === "Funnel" && <FunnelTab highlightStep={step} />}
        {active === "Revenue" && <RevenueTab highlight={highlight} />}
        {active === "System" && <SystemTab />}
        {active === "Behavior" && <BehaviorTab />}
        {active === "Messages" && <MessagesTab />}
        {active === "Feature Value" && <FeatureValueTab />}
        {active === "Segments" && <SegmentTab />}
        {active === "Cohort" && <CohortTab />}
      </div>
    </div>
  );
}

// ─── Tab Bar ───

function TabBar({
  active,
  onSelect,
}: {
  active: TabName;
  onSelect: (tab: TabName) => void;
}): React.JSX.Element {
  return (
    <div style={s.tabBar}>
      {TABS.map((tab) => (
        <button
          key={tab}
          type="button"
          onClick={() => onSelect(tab)}
          style={tab === active ? { ...s.tab, ...s.tabActive } : s.tab}
        >
          {t(TAB_I18N_KEYS[tab])}
        </button>
      ))}
    </div>
  );
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  tabBar: {
    display: "flex",
    gap: 0,
    borderBottom: "1px solid var(--color-border)",
    marginBottom: 24,
  },
  tab: {
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    borderBottom: "2px solid transparent",
    marginBottom: -1,
    transition: "color 0.15s, border-color 0.15s",
  },
  tabActive: {
    color: "var(--color-text)",
    fontWeight: 600,
    borderBottomColor: "var(--color-primary)",
  },
  content: {
    minHeight: 200,
  },
};
